import { fetchJSON as fetchAPI } from './client';

export type FBAOpsDashboard = {
  tenant_id: string;
  total_skus: number;
  available_units: number;
  inbound_units: number;
  reserved_units: number;
  active_shipments: number;
  delayed_shipments: number;
  avg_health_score: number;
  avg_warehouse_score: number;
  capacity_utilization_pct: number;
  active_reconciliations: number;
  active_recommendations: number;
  active_sync_jobs: number;
  generated_at?: string;
};

export type InventoryRecord = {
  id: string;
  sku: string;
  asin?: string;
  fnsku?: string;
  title?: string;
  marketplace?: string;
  fulfillment_center?: string;
  inventory_type: string;
  quantity: number;
  days_of_cover: number;
  inventory_age_days: number;
  health_score: number;
  is_critical: boolean;
  is_slow_moving: boolean;
  updated_at?: string;
};

export type InboundShipmentRecord = {
  id: string;
  shipment_id: string;
  shipment_name?: string;
  status: string;
  destination_fc?: string;
  carrier?: string;
  tracking_number?: string;
  box_count: number;
  unit_count: number;
  received_units: number;
  shipped_units: number;
  estimated_arrival?: string;
  health_score: number;
  readiness_score: number;
  created_at?: string;
  updated_at?: string;
};

export type BoxRecord = {
  id: string;
  shipment_id: string;
  box_id: string;
  weight_lbs: number;
  length_in: number;
  width_in: number;
  height_in: number;
  unit_count: number;
  label_printed: boolean;
  is_valid: boolean;
};

export type WarehouseCapacityRecord = {
  id: string;
  fulfillment_center: string;
  storage_type?: string;
  capacity_limit: number;
  current_utilization: number;
  utilization_pct: number;
  restock_limit: number;
  inbound_limit: number;
  risk_level?: string;
};

export type ReceivingRecord = {
  id: string;
  shipment_id: string;
  sku: string;
  shipped_qty: number;
  received_qty: number;
  discrepancy: number;
  status: string;
  issue?: string;
};

export type InventoryHealthRecord = {
  id: string;
  sku: string;
  fnsku?: string;
  health_score: number;
  warehouse_score: number;
  days_of_cover: number;
  turnover_rate: number;
  storage_fee_risk: number;
  issue?: string;
  severity?: string;
};

export type FBARecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  sku?: string;
  shipment_id?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

export type SyncJobRecord = {
  id: string;
  mode: string;
  status: string;
  items_synced: number;
  shipments_synced: number;
  items_failed: number;
  started_at?: string;
  completed_at?: string;
};

const API_BASE = import.meta.env.VITE_FBAOPS_API ?? '/api/v1/fbaops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const fbaopsApi = {
  dashboard: () => fetchJSON<FBAOpsDashboard>('/dashboard'),
  listInventory: () => fetchJSON<InventoryRecord[]>('/inventory'),
  listShipments: () => fetchJSON<InboundShipmentRecord[]>('/shipments'),
  listBoxes: (shipmentId?: string) =>
    fetchJSON<BoxRecord[]>(shipmentId ? `/boxes?shipment_id=${shipmentId}` : '/boxes'),
  listCapacity: () => fetchJSON<WarehouseCapacityRecord[]>('/capacity'),
  listReceiving: () => fetchJSON<ReceivingRecord[]>('/receiving'),
  listHealth: () => fetchJSON<InventoryHealthRecord[]>('/health'),
  listRecommendations: () => fetchJSON<FBARecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<FBARecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<SyncJobRecord[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
